/**
 * Pure width computation for column-header filter popovers (plan "Spaltenkopf-Filterung und
 * Sortieranzeige", Phase 2.3, Standing constraint 9): a range filter's "von" and "bis" fields sit
 * next to each other in ONE row, never stacked and never wrapped. Task #1609's overflow came from a
 * column-direction `Grid2` that wrapped into extra side-by-side columns once its content exceeded the
 * panel's fixed height, pulling every field far past the panel's right edge — see
 * `OrderFilterSheetFields.tsx`'s former `wrap="nowrap"` comment. Computing the width from field
 * count, longest label, and suffix length instead of a guessed constant keeps the same class of
 * overflow from returning once "von" and "bis" sit side by side instead of stacked, and makes the
 * guard checkable in a fast Vitest run rather than only in a slower, later E2E screenshot.
 *
 * Pure functions of string length, no DOM measurement: this module has no React and no access to the
 * actual rendered font, so the constants below are a coarse, deliberately generous per-character
 * budget, not a pixel-accurate text measurement.
 */

/** Minimum width (px) any single filter field is given, regardless of its label length. */
const FIELD_MIN_WIDTH_PX = 140;

/** Approximate width (px) of one character at the filter fields' font size. */
const CHAR_WIDTH_PX = 8;

/** Padding (px) added around a field's label/suffix budget for the input's own chrome (border,
 *  inner padding) so a label sized right at the computed width does not visually touch it. */
const FIELD_CHROME_PADDING_PX = 32;

/** Gap (px) between adjacent fields placed side by side in one row. */
const FIELD_GAP_PX = 16;

/**
 * Minimum width (px) one filter field needs to show its label and its suffix (the unit suffixes
 * "Stk.", "mm", "km", and the currency symbol `CurrencyInputField` defaults to — wired by the request
 * range filters in Phase 3.2) without wrapping or truncation.
 */
export function fieldMinWidth(label: string, suffix?: string): number {
    const text = suffix ? `${label} ${suffix}` : label;
    return Math.max(FIELD_MIN_WIDTH_PX, text.length * CHAR_WIDTH_PX + FIELD_CHROME_PADDING_PX);
}

/**
 * Minimum width (px) for `fieldCount` fields placed side by side in one `ColumnFilterRow` (Standing
 * constraint 9), sized off the row's own longest occurring label and suffix rather than off each
 * individual field — every field in a `ColumnFilterRow` uses the same computed
 * width, so the row's widest label already bounds every other field in it. Grows monotonically with
 * `fieldCount`: one more field can only add its own width and one more gap, never shrink the total.
 *
 * This is the row's own content width, nothing more: it carries no reserve for surrounding popover
 * chrome. `DataTableColumnHeader.tsx`'s `padding: 2` around the filter section is the sole source of
 * that reserve, adding it here as well would double it without either side knowing about the other.
 */
export function popoverMinWidth(fieldCount: number, longestLabel: string, suffix?: string): number {
    const perFieldWidth = fieldMinWidth(longestLabel, suffix);
    const gapsWidth = Math.max(0, fieldCount - 1) * FIELD_GAP_PX;
    return fieldCount * perFieldWidth + gapsWidth;
}
